import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../../../components/Breadcrumb';
import Page from '../../../../components/Page';
import PrimaryButton from '../../../../components/PrimaryButton';
import client from '../../../../lib/feathers';

/**
 * /admin/users/:uid/library-import
 *
 * Lets an admin upload a CellarTracker CSV for a target user. After upload,
 * the API parses + runs matching and returns the import id; we redirect to
 * /admin/library-imports/:id where the admin reviews and executes.
 *
 * Also lists any prior imports for this user so admin can pick one up.
 */
export default function UserLibraryImport() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [importUser, setImportUser] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      const res = await client.service('admin/library-import').find({
        query: { userId: uid, $limit: 25 },
      });
      setHistory(res.data || []);
    } catch (e) {
      // Best-effort — empty history isn't an error worth blocking on.
    }
    try {
      const u = await client.service('admin/user').get(uid);
      setImportUser(u);
    } catch (_) { /* ignore — breadcrumb falls back to uuid slice */ }
  }, [uid]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || uploading) return;
    setUploading(true);
    setError(null);
    try {
      // CellarTracker exports are Latin-1; read as ArrayBuffer + decode so
      // accented producer names ("Résonance") survive the trip to the API.
      const buf = await file.arrayBuffer();
      let csv;
      try {
        csv = new TextDecoder('utf-8', { fatal: true }).decode(buf);
      } catch (_) {
        csv = new TextDecoder('iso-8859-1').decode(buf);
      }
      const created = await client.service('admin/library-import').create({
        userId: uid,
        csv,
        filename: file.name,
      });
      navigate(`/admin/library-imports/${created.id}`);
    } catch (e2) {
      setError(e2.message || 'Upload failed');
    }
    setUploading(false);
  };

  return (
    <Page title="Import Library">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <Breadcrumb elements={[
          { name: 'Users', href: '/admin/users' },
          {
            name: importUser
              ? `${importUser.firstName || ''} ${importUser.lastName || ''}`.trim() || importUser.username || uid.slice(0, 8)
              : uid.slice(0, 8),
            href: `/admin/users/${uid}`,
          },
          { name: 'Import Library' },
        ]}
        />
      </div>

      <div className="py-4 max-w-2xl">
        <h3 className="text-lg font-medium mb-2">Upload CellarTracker CSV</h3>
        <p className="text-sm text-gray-500 mb-4">
          Upload the user&apos;s CSV export from cellartracker.com. The server parses it,
          fuzzy-matches each row to existing listings + libations, and stages
          everything for review. Nothing is created until you click Execute on the
          next page.
        </p>
        <form onSubmit={handleUpload} className="space-y-3">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block"
          />
          <PrimaryButton type="submit" disabled={!file || uploading}>
            {uploading ? 'Parsing…' : 'Upload + Preview'}
          </PrimaryButton>
          {error && <div className="text-sm text-red-700">{error}</div>}
        </form>
      </div>

      {history.length > 0 && (
        <div className="py-4">
          <h3 className="text-lg font-medium mb-2">Prior imports</h3>
          <table className="w-full text-sm">
            <thead className="text-left text-gray-500 text-xs uppercase">
              <tr>
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Filename</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Rows</th>
                <th className="py-2 pr-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr
                  key={h.id}
                  className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/admin/library-imports/${h.id}`)}
                >
                  <td className="py-2 pr-3">{new Date(h.createdAt).toLocaleString()}</td>
                  <td className="py-2 pr-3">{h.filename || '—'}</td>
                  <td className="py-2 pr-3">{h.status}</td>
                  <td className="py-2 pr-3">{h.summary?.rowCount ?? '—'}</td>
                  <td className="py-2 pr-3">{h.summary?.createdEntries ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Page>
  );
}
